export type AssetMeta = {
  id: string
  label: string
  name: string
  assetClass?: 'equity' | 'bond' | 'commodity' | 'crypto' | 'macro' | 'fx'
  tier?: 'tier0' | 'tier1' | 'tier2' | 'tier3'
  yahooSymbol?: string
}

// Catalog of assets the UI can recognize/search. The backend can return a subset;
// the UI should gracefully handle missing data.
export const ASSET_CATALOG: AssetMeta[] = [
  // Existing (current)
  { id: 'SPY', label: 'SPY', name: 'S&P 500', assetClass: 'equity', tier: 'tier0' },
  { id: 'QQQ', label: 'QQQ', name: 'Nasdaq 100', assetClass: 'equity', tier: 'tier0' },
  { id: 'GLD', label: 'GLD', name: 'Gold', assetClass: 'commodity', tier: 'tier0' },
  { id: 'TLT', label: 'TLT', name: 'Long Bonds', assetClass: 'bond', tier: 'tier0' },
  { id: 'USO', label: 'USO', name: 'Oil (WTI)', assetClass: 'commodity', tier: 'tier0' },
  { id: 'BTC-USD', label: 'BTC', name: 'Bitcoin', assetClass: 'crypto', tier: 'tier0' },
  { id: 'ETH-USD', label: 'ETH', name: 'Ethereum', assetClass: 'crypto', tier: 'tier0' },

  // Tier 1 — high intent
  { id: 'AAPL', label: 'AAPL', name: 'Apple', assetClass: 'equity', tier: 'tier1' },
  { id: 'MSFT', label: 'MSFT', name: 'Microsoft', assetClass: 'equity', tier: 'tier1' },
  { id: 'NVDA', label: 'NVDA', name: 'NVIDIA', assetClass: 'equity', tier: 'tier1' },
  { id: 'TSLA', label: 'TSLA', name: 'Tesla', assetClass: 'equity', tier: 'tier1' },
  { id: 'AMZN', label: 'AMZN', name: 'Amazon', assetClass: 'equity', tier: 'tier1' },
  { id: 'META', label: 'META', name: 'Meta', assetClass: 'equity', tier: 'tier1' },
  { id: 'GOOGL', label: 'GOOGL', name: 'Alphabet', assetClass: 'equity', tier: 'tier1' },
  { id: 'JPM', label: 'JPM', name: 'JPMorgan', assetClass: 'equity', tier: 'tier1' },
  { id: 'BAC', label: 'BAC', name: 'Bank of America', assetClass: 'equity', tier: 'tier1' },
  { id: 'XLF', label: 'XLF', name: 'Financials', assetClass: 'equity', tier: 'tier1' },
  { id: '^VIX', label: 'VIX', name: 'VIX', assetClass: 'macro', tier: 'tier1', yahooSymbol: '^VIX' },

  // Tier 1 — additional
  { id: 'BRK-B', label: 'BRK.B', name: 'Berkshire Hathaway', assetClass: 'equity', tier: 'tier1' },
  { id: 'UNH', label: 'UNH', name: 'UnitedHealth', assetClass: 'equity', tier: 'tier1' },
  { id: 'XOM', label: 'XOM', name: 'Exxon Mobil', assetClass: 'equity', tier: 'tier1' },
  { id: 'JNJ', label: 'JNJ', name: 'Johnson & Johnson', assetClass: 'equity', tier: 'tier1' },
  { id: 'PG', label: 'PG', name: 'Procter & Gamble', assetClass: 'equity', tier: 'tier1' },
  { id: 'KO', label: 'KO', name: 'Coca-Cola', assetClass: 'equity', tier: 'tier1' },
  { id: 'COST', label: 'COST', name: 'Costco', assetClass: 'equity', tier: 'tier1' },
  { id: 'AVGO', label: 'AVGO', name: 'Broadcom', assetClass: 'equity', tier: 'tier1' },
  { id: 'NFLX', label: 'NFLX', name: 'Netflix', assetClass: 'equity', tier: 'tier1' },
  { id: 'ADBE', label: 'ADBE', name: 'Adobe', assetClass: 'equity', tier: 'tier1' },
  { id: 'ORCL', label: 'ORCL', name: 'Oracle', assetClass: 'equity', tier: 'tier1' },

  // Tier 2 — thematic
  { id: 'IWM', label: 'IWM', name: 'Russell 2000', assetClass: 'equity', tier: 'tier2' },
  { id: 'HYG', label: 'HYG', name: 'High Yield Credit', assetClass: 'equity', tier: 'tier2' },
  { id: 'DX-Y.NYB', label: 'DXY', name: 'US Dollar Index', assetClass: 'macro', tier: 'tier2', yahooSymbol: 'DX-Y.NYB' },
  { id: 'EURUSD=X', label: 'EURUSD', name: 'EUR/USD', assetClass: 'fx', tier: 'tier2', yahooSymbol: 'EURUSD=X' },

  // Tier 2 — additional
  { id: 'SMH', label: 'SMH', name: 'Semiconductors (ETF)', assetClass: 'equity', tier: 'tier2' },
  { id: 'ARKK', label: 'ARKK', name: 'Innovation (ETF)', assetClass: 'equity', tier: 'tier2' },
  { id: 'UUP', label: 'UUP', name: 'US Dollar (ETF)', assetClass: 'macro', tier: 'tier2' },
  { id: 'EEM', label: 'EEM', name: 'Emerging Markets (ETF)', assetClass: 'equity', tier: 'tier2' },
  { id: 'EFA', label: 'EFA', name: 'Developed Markets (ETF)', assetClass: 'equity', tier: 'tier2' },
  { id: 'GDX', label: 'GDX', name: 'Gold Miners (ETF)', assetClass: 'equity', tier: 'tier2' },
  { id: 'IEF', label: 'IEF', name: '7–10Y Treasuries (ETF)', assetClass: 'bond', tier: 'tier2' },
  { id: 'SHY', label: 'SHY', name: '1–3Y Treasuries (ETF)', assetClass: 'bond', tier: 'tier2' },
  { id: 'SLV', label: 'SLV', name: 'Silver (ETF)', assetClass: 'commodity', tier: 'tier2' },

  // Tier 3 — sector ETFs
  { id: 'XLE', label: 'XLE', name: 'Energy', assetClass: 'equity', tier: 'tier3' },
  { id: 'XLK', label: 'XLK', name: 'Technology', assetClass: 'equity', tier: 'tier3' },
  { id: 'XLV', label: 'XLV', name: 'Healthcare', assetClass: 'equity', tier: 'tier3' },
  { id: 'XLB', label: 'XLB', name: 'Materials', assetClass: 'equity', tier: 'tier3' },
  { id: 'XLI', label: 'XLI', name: 'Industrials', assetClass: 'equity', tier: 'tier3' },
  { id: 'XLY', label: 'XLY', name: 'Consumer Discretionary', assetClass: 'equity', tier: 'tier3' },
  { id: 'XLP', label: 'XLP', name: 'Consumer Staples', assetClass: 'equity', tier: 'tier3' },
  { id: 'XLU', label: 'XLU', name: 'Utilities', assetClass: 'equity', tier: 'tier3' },
  { id: 'XLRE', label: 'XLRE', name: 'Real Estate', assetClass: 'equity', tier: 'tier3' },
  { id: 'XLC', label: 'XLC', name: 'Communication Services', assetClass: 'equity', tier: 'tier3' }
]

export const POPULAR_ASSET_IDS: string[] = ['SPY', 'BTC-USD', 'AAPL', 'GLD', 'NVDA']

export function getAssetMeta(assetId: string): AssetMeta | undefined {
  return ASSET_CATALOG.find((a) => a.id === assetId)
}

export function assetLabel(assetId: string): string {
  return getAssetMeta(assetId)?.label || assetId
}

export function assetName(assetId: string): string {
  return getAssetMeta(assetId)?.name || assetId
}
